/**
 * Единственная точка доступа к данным. Компоненты не знают, откуда они
 * приходят.
 *
 * По умолчанию в собранном образе — живой источник: коллектор на
 * liquorice-mm-2 хвостиком читает WAL бота и выгружает срез в GCS раз в 2 с,
 * отсюда его забирает `PollingDataSource` (см. data/live.ts). Без
 * VITE_LIVE_SNAPSHOT_URL (обычный `npm run dev` без своего .env) остаётся мок
 * — так локальная разработка не зависит от доступа к боевой VM.
 *
 * Управляющих ручек здесь нет и не будет: интерфейс — зеркало, не пульт.
 * Подключение кошелька, supply и repay появятся отдельным транспортом
 * (см. wallet/README.md), а не через этот интерфейс.
 */
import { useEffect, useState } from 'react'
import type { Snapshot } from '../types'
import { buildSnapshot } from './mock/generate'
import { reviveSnapshot } from './live'

export type ConnectionState = 'connecting' | 'live' | 'stale' | 'down'

export interface DataSource {
  /** Возвращает функцию отписки. */
  subscribe(onSnapshot: (s: Snapshot) => void): () => void
  connectionState(): ConnectionState
}

export class MockDataSource implements DataSource {
  private tick = 0
  private state: ConnectionState = 'live'

  constructor(private readonly periodMs = 2000) {}

  subscribe(onSnapshot: (s: Snapshot) => void): () => void {
    onSnapshot(buildSnapshot(this.tick))
    const id = window.setInterval(() => {
      this.tick += 1
      onSnapshot(buildSnapshot(this.tick))
    }, this.periodMs)
    return () => window.clearInterval(id)
  }

  connectionState(): ConnectionState {
    return this.state
  }
}

/**
 * Живой источник. Коллектор льёт снапшот раз в 2 с — забираем тем же ритмом
 * поллингом, а не WebSocket: объект маленький (десятки КБ), GCS не умеет
 * push, а разница в задержке против WS на этом периоде не видна глазом.
 */
export class PollingDataSource implements DataSource {
  private state: ConnectionState = 'connecting'

  constructor(
    private readonly url: string,
    private readonly periodMs = 1000,
  ) {}

  subscribe(onSnapshot: (s: Snapshot) => void): () => void {
    let stopped = false
    let timer: number | undefined

    const tick = async () => {
      if (stopped) return
      try {
        const res = await fetch(`${this.url}?_=${Date.now()}`, { cache: 'no-store' })
        if (!res.ok) throw new Error(`snapshot fetch: HTTP ${res.status}`)
        const raw = await res.json()
        this.state = 'live'
        onSnapshot(reviveSnapshot(raw))
      } catch (err) {
        this.state = this.state === 'connecting' ? 'down' : 'stale'
        console.error('[live] snapshot fetch failed', err)
      } finally {
        if (!stopped) timer = window.setTimeout(tick, this.periodMs)
      }
    }

    tick()
    return () => {
      stopped = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }

  connectionState(): ConnectionState {
    return this.state
  }
}

const LIVE_SNAPSHOT_URL = import.meta.env.VITE_LIVE_SNAPSHOT_URL as string | undefined

const source: DataSource = LIVE_SNAPSHOT_URL
  ? new PollingDataSource(LIVE_SNAPSHOT_URL)
  : new MockDataSource()

export function useSnapshot(): Snapshot | null {
  const [snap, setSnap] = useState<Snapshot | null>(null)
  useEffect(() => source.subscribe(setSnap), [])
  return snap
}
